// Shared AI assist for the reply composer and the Compose dialog.
//
// One hook owns:
//   - the Draft and Polish mutations (with an optional instruction / preset)
//   - the images + links round-trip (ai-html-tokens), so Polish never strips them
//   - a real undo stack. The editor's native Cmd+Z can't undo `setHtml`, so every
//     AI change snapshots the previous HTML first and exposes `undo()`.
//   - friendly handling of Claude overload / rate-limit errors
//
// CLAUDE.md section 2: this only fills the composer for a human to review. It
// never sends anything.

import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toastError } from '@/lib/toast';
import {
  postDraftReply,
  postPolishReply,
  type DraftReplyResponse,
  type PolishPreset,
  type PolishReplyResponse,
} from '@/lib/api';
import { rehydrateFromAi, tokenizeForAi, type AiTokenMap } from '@/lib/ai-html-tokens';
import { plainToHtmlParagraphs } from '@/lib/template-render';

const FORWARD_MARKER = '---------- Forwarded message ----------';
const MAX_UNDO = 5;

/** Forward mode pre-fills the original message below a marker line. Only the
 *  part above it is the agent's own writing; the quoted block must never be fed
 *  to (or replaced by) the AI. */
export function splitForwarded(text: string): { head: string; tail: string } {
  const i = text.indexOf(FORWARD_MARKER);
  if (i < 0) return { head: text, tail: '' };
  return { head: text.slice(0, i).trim(), tail: text.slice(i) };
}

export const POLISH_PRESET_LABELS: Record<PolishPreset, string> = {
  fix_grammar: 'Fix grammar only',
  shorter: 'Shorter',
  friendlier: 'Friendlier',
  formal: 'More formal',
  firmer: 'Firmer',
  plain: 'Plain English',
};

export interface UseAiAssistArgs {
  /** Omit in the Compose dialog (no ticket yet). */
  ticketId?: string;
  /** Compose only: shown to the model as the recipient. */
  recipient?: string;
  /** Current editor HTML. */
  getBodyHtml: () => string;
  /** Replace the editor content (must update both the editor and React state). */
  applyHtml: (html: string) => void;
}

interface RunVars {
  instruction: string;
  preset?: PolishPreset;
  head: string;
  tail: string;
  previous: string;
  map: AiTokenMap;
}

function friendlyOverload(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  // Anthropic 529 overloaded_error / 429 / 5xx blips. The Edge Function already
  // retried with backoff before surfacing this.
  return /\b(529|503|502|504)\b|overloaded|rate.?limit/i.test(msg);
}

// Whether the AI options bar is open. A per-viewer convenience only, so it
// lives in localStorage and every access is guarded (private windows throw).
const AI_BAR_KEY = 'pa-ai-bar-open';

export function useAiBarOpen(): [boolean, () => void] {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AI_BAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(AI_BAR_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  return [open, toggle];
}

export function useAiAssist({ ticketId, recipient, getBodyHtml, applyHtml }: UseAiAssistArgs) {
  const undoStack = useRef<string[]>([]);
  const [undoDepth, setUndoDepth] = useState(0);

  const pushUndo = (html: string) => {
    undoStack.current = [...undoStack.current, html].slice(-MAX_UNDO);
    setUndoDepth(undoStack.current.length);
  };

  const undo = useCallback(() => {
    const prev = undoStack.current[undoStack.current.length - 1];
    if (prev === undefined) return;
    undoStack.current = undoStack.current.slice(0, -1);
    setUndoDepth(undoStack.current.length);
    applyHtml(prev);
    toast.info('Restored your previous text');
  }, [applyHtml]);

  const finish = (aiText: string, vars: RunVars, headline: string, extra: string[]) => {
    const { html, restoredLinks, restoredImages } = rehydrateFromAi(aiText, vars.map);
    const notes = [...extra];
    if (restoredLinks.length > 0) {
      notes.push(`Kept ${restoredLinks.length} link${restoredLinks.length === 1 ? '' : 's'} the AI dropped; check where they sit`);
    }
    if (restoredImages > 0) {
      notes.push(`Kept ${restoredImages} image${restoredImages === 1 ? '' : 's'} the AI dropped; check where they sit`);
    }
    // Forward mode: put the quoted original back underneath.
    const full = vars.tail ? html + plainToHtmlParagraphs(vars.tail) : html;
    pushUndo(vars.previous);
    applyHtml(full);
    toast.success(headline, {
      description: notes.length > 0 ? notes.join('. ') : undefined,
      duration: 8000,
      action: { label: 'Undo', onClick: undo },
    });
  };

  const draftMut = useMutation({
    mutationFn: (v: RunVars) =>
      postDraftReply({
        ticketId,
        recipient,
        instruction: v.instruction,
        agentNotes: v.head,
      }),
    onSuccess: (res: DraftReplyResponse, v) => {
      if (!res.ok || !res.draft) {
        toast.error(`Draft failed: ${res.error ?? 'no text returned'}`);
        return;
      }
      const used: string[] = [];
      if (v.head) used.push('your notes');
      if (res.context?.kb) used.push(`${res.context.kb} KB article${res.context.kb === 1 ? '' : 's'}`);
      if (res.context?.notes) used.push(`${res.context.notes} internal note${res.context.notes === 1 ? '' : 's'}`);
      finish(
        res.draft,
        v,
        'Draft inserted. Review and edit before sending.',
        used.length > 0 ? [`Used ${used.join(', ')}`] : [],
      );
    },
    onError: (err: unknown) => {
      if (friendlyOverload(err)) {
        toast.error('Claude is briefly overloaded. Give it a few seconds and try Draft again.');
        return;
      }
      toastError('Draft failed', err);
    },
  });

  const polishMut = useMutation({
    mutationFn: (v: RunVars) =>
      postPolishReply({
        ticketId,
        draft: v.head,
        preset: v.preset,
        instruction: v.instruction,
      }),
    onSuccess: (res: PolishReplyResponse, v) => {
      if (!res.ok || !res.polished) {
        toast.error(`Polish failed: ${res.error ?? 'no text returned'}`);
        return;
      }
      finish(
        res.polished,
        v,
        v.preset ? `Done: ${POLISH_PRESET_LABELS[v.preset]}` : 'Polished',
        [],
      );
    },
    onError: (err: unknown) => {
      if (friendlyOverload(err)) {
        // Bespoke copy: names the cause and the remedy; a convenience feature, so
        // a short dismissible toast is right.
        toast.error('Claude is briefly overloaded. Give it a few seconds and try again.');
        return;
      }
      toastError('Polish failed', err);
    },
  });

  const snapshot = (instruction: string, preset?: PolishPreset): RunVars => {
    const previous = getBodyHtml();
    const { text, map } = tokenizeForAi(previous);
    const { head, tail } = splitForwarded(text);
    return { instruction: instruction.trim(), preset, head, tail, previous, map };
  };

  const busy = draftMut.isPending || polishMut.isPending;

  /** Write a reply. The agent's existing text (if any) becomes the brief. */
  const draft = (opts: { instruction?: string } = {}) => {
    if (busy) return;
    const v = snapshot(opts.instruction ?? '');
    if (!ticketId && !v.instruction && !v.head) {
      toast.error('Tell the AI what the email should say first. Type it in the AI bar, or jot rough notes in the body.');
      return;
    }
    draftMut.mutate(v);
  };

  /** Rewrite what the agent has written, optionally with a preset / instruction. */
  const polish = (opts: { instruction?: string; preset?: PolishPreset } = {}) => {
    if (busy) return;
    const v = snapshot(opts.instruction ?? '', opts.preset);
    if (!v.head) {
      toast.error('Type something first. There is nothing to polish.');
      return;
    }
    polishMut.mutate(v);
  };

  return {
    draft,
    polish,
    undo,
    canUndo: undoDepth > 0,
    drafting: draftMut.isPending,
    polishing: polishMut.isPending,
    busy,
  };
}
